import { ChangeEvent, useEffect, useRef, useState } from "react";
import NoImage from "../../assets/img/avatars/no-image-icon.png";
import { reqFunction } from "utils/API";
import { renderToast } from "utils/globalUtils";

interface FileUploaderProps {
  preview: string | null;
  setPreview: React.Dispatch<React.SetStateAction<string | null>>;
  uploaderType: "projectImage" | "profilePicture";
  uploadEndpoint: string;
}

type UploadLimits = {
  max_bytes: number; // required from backend
  types: string[]; // required from backend (extensions and/or MIME)
};

const FileUploader = (props: FileUploaderProps) => {
  const { preview, setPreview, uploaderType, uploadEndpoint } = props;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [limits, setLimits] = useState<UploadLimits | null>(null);
  const [limitsError, setLimitsError] = useState<string | null>(null);
  const limitsLoaded = !!limits && !limitsError;

  // Webcam state/refs
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [currentFacing, setCurrentFacing] = useState<"user" | "environment">(
    "user"
  );

  const [videoDeviceIds, setVideoDeviceIds] = useState<string[]>([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState<number | null>(
    null
  );

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ---------- Fetch limits from backend (Config/admin/getServerSetups) ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await reqFunction("Config/admin/getServerSetups", {});
        const cfg = res?.data?.config;
        const ul = cfg?.uploads;

        const max_bytes = ul?.max_bytes;
        const types = ul?.types;

        if (
          typeof max_bytes === "number" &&
          max_bytes > 0 &&
          Array.isArray(types) &&
          types.length > 0
        ) {
          if (!cancelled) {
            setLimits({ max_bytes, types });
            setLimitsError(null);
          }
        } else {
          if (!cancelled) {
            setLimits(null);
            setLimitsError(
              "محدودیت‌های آپلود در سرور به‌درستی پیکربندی نشده‌اند."
            );
            renderToast("محدودیت‌های آپلود در سرور تنظیم نشده‌اند.", "warn");
          }
        }
      } catch {
        if (!cancelled) {
          setLimits(null);
          setLimitsError("عدم دسترسی به تنظیمات سرور.");
          renderToast("خطا در دریافت تنظیمات آپلود از سرور.", "err");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Helpers ----------
  const humanBytes = (n: number) => {
    const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت"];
    let i = 0,
      v = n;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v % 1 === 0 ? v : v.toFixed(1)} ${units[i]}`;
  };

  // token: ".jpg" or "image/png" or "image/*"
  const fileMatchesToken = (file: File, token: string) => {
    const t = token.trim().toLowerCase();
    if (!t) return false;

    const name = file.name.toLowerCase();
    const mime = (file.type || "").toLowerCase();

    if (t.startsWith(".")) {
      return name.endsWith(t); // extension match
    }
    if (t.includes("/")) {
      if (t.endsWith("/*")) {
        const major = t.split("/")[0];
        return mime.startsWith(`${major}/`);
      }
      return mime === t; // exact mime
    }
    return false;
  };

  const isTypeAllowed = (file: File) =>
    !!limits && limits.types.some((tok) => fileMatchesToken(file, tok));

  const acceptAttr = (() => {
    if (!limits) return ""; // nothing until loaded
    const exts = limits.types.filter((t) => t.trim().startsWith("."));
    // only extensions belong in accept= for best UX
    return exts.join(",");
  })();

  const explainAllowedTypes = () => (limits ? limits.types.join("، ") : "");

  // ---------- File picker ----------
  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!limitsLoaded) {
      renderToast(
        limitsError || "امکان آپلود تا زمان بارگذاری تنظیمات وجود ندارد.",
        "warn"
      );
      return;
    }

    // Type check (only backend)
    if (!isTypeAllowed(file)) {
      renderToast(
        `نوع فایل مجاز نیست. انواع مجاز: ${explainAllowedTypes()}`,
        "warn"
      );
      return;
    }

    // Size check (only backend)
    if (file.size > limits.max_bytes) {
      renderToast(
        `حجم فایل زیاد است. حداکثر مجاز: ${humanBytes(
          limits.max_bytes
        )} (حجم فعلی: ${humanBytes(file.size)})`,
        "warn"
      );
      return;
    }

    setSelectedFile(file);

    if (uploaderType === "profilePicture") {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      const data = new FormData();
      data.append("file", file);
      const response = await reqFunction(uploadEndpoint, data);

      if (response?.code === 200) {
        setPreview(response.data.fileurl);
      } else {
        renderToast(
          response?.farsi_message
            ? response.farsi_message
            : "در بارگذاری تصویر خطایی رخ داد",
          "err"
        );
        console.log(selectedFile);
      }
    }
  };

  // ---------- Webcam helpers ----------
  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const attachToVideo = async (stream: MediaStream) => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    setIsReady(false);
    await new Promise<void>((resolve) => {
      const onLoaded = async () => {
        try {
          await video.play();
          setIsReady(true);
        } catch {
          setIsReady(false);
        }
        resolve();
      };
      if (video.readyState >= 1) onLoaded();
      else video.addEventListener("loadedmetadata", onLoaded, { once: true });
    });
  };

  const enumerateVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === "videoinput");
      setVideoDeviceIds(vids.map((d) => d.deviceId));
    } catch {
      /* ignore until permission */
    }
  };

  const startStream = async (opts: {
    deviceId?: string;
    facingMode?: "user" | "environment";
  }) => {
    stopStream();
    try {
      const constraints: MediaStreamConstraints = {
        video: opts.deviceId
          ? { deviceId: { exact: opts.deviceId } }
          : { facingMode: { ideal: opts.facingMode ?? "user" } },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      await attachToVideo(stream);
      await enumerateVideoDevices();
      return true;
    } catch {
      return false;
    }
  };

  const openCamera = async () => setIsCameraOpen(true);
  const closeCamera = () => {
    stopStream();
    setIsCameraOpen(false);
    setIsReady(false);
    setIsSwitching(false);
  };

  useEffect(() => {
    if (!isCameraOpen) return;
    let cancelled = false;
    const boot = async () => {
      let ok = await startStream({ facingMode: "user" });
      if (!ok) ok = await startStream({ facingMode: "environment" });

      if (!ok && !cancelled) {
        renderToast("عدم دسترسی به دوربین. اجازه دسترسی را بررسی کنید.", "err");
        closeCamera();
      } else {
        setCurrentFacing(ok ? "user" : "environment");
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  const switchCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      renderToast("مرورگر از تعویض دوربین پشتیبانی نمی‌کند.", "warn");
      return;
    }
    setIsSwitching(true);
    try {
      if (videoDeviceIds.length >= 2) {
        const nextIndex =
          currentDeviceIndex === null
            ? 1
            : (currentDeviceIndex + 1) % videoDeviceIds.length;
        const ok = await startStream({ deviceId: videoDeviceIds[nextIndex] });
        if (ok) {
          setCurrentDeviceIndex(nextIndex);
          setIsSwitching(false);
          return;
        }
      }
      const nextFacing = currentFacing === "user" ? "environment" : "user";
      const ok = await startStream({ facingMode: nextFacing });
      if (ok) setCurrentFacing(nextFacing);
    } catch {
      renderToast("تعویض دوربین با خطا مواجه شد.", "err");
    } finally {
      setIsSwitching(false);
    }
  };

  useEffect(() => {
    const syncActiveDeviceIndex = async () => {
      if (!streamRef.current || videoDeviceIds.length === 0) return;
      const track = streamRef.current.getVideoTracks()[0];
      const settings = track.getSettings?.();
      if (settings?.deviceId) {
        const idx = videoDeviceIds.findIndex((id) => id === settings.deviceId);
        if (idx >= 0) setCurrentDeviceIndex(idx);
      } else setCurrentDeviceIndex(null);
    };
    syncActiveDeviceIndex();
  }, [videoDeviceIds, streamRef.current]);

  // Produce JPEG trying to stay under server max (uses only server value)
  const canvasToSizedBlob = async (canvas: HTMLCanvasElement) => {
    if (!limits) return null;
    const qualities = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35, 0.3];
    for (const q of qualities) {
      const dataUrl = canvas.toDataURL("image/jpeg", q);
      const blob = await (await fetch(dataUrl)).blob();
      if (blob.size <= limits.max_bytes) return blob;
    }
    return null; // if still too big, we’ll warn and abort
  };

  const handleCapture = async () => {
    if (!limitsLoaded) {
      renderToast(
        limitsError || "امکان ثبت تصویر تا زمان بارگذاری تنظیمات وجود ندارد.",
        "warn"
      );
      return;
    }

    const video = videoRef.current;
    if (!video || !isReady) {
      renderToast("ویدیو آماده نیست. کمی صبر کنید.", "warn");
      return;
    }

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      await new Promise<void>((resolve) =>
        video.addEventListener("loadeddata", () => resolve(), { once: true })
      );
      if (video.videoWidth === 0) {
        renderToast("امکان دریافت فریم از دوربین نیست.", "err");
        return;
      }
    }

    const canvas = canvasRef.current || document.createElement("canvas");
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;

    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToSizedBlob(canvas);
    if (!blob) {
      renderToast(
        `تصویر بزرگ‌تر از حد مجاز است. حداکثر: ${humanBytes(
          limits!.max_bytes
        )}.`,
        "warn"
      );
      return;
    }

    const webcamFile = new File([blob], "webcam.jpg", { type: "image/jpeg" });
    if (!isTypeAllowed(webcamFile)) {
      renderToast(
        `نوع تصویر دوربین مجاز نیست. انواع مجاز: ${explainAllowedTypes()}`,
        "warn"
      );
      return;
    }

    if (uploaderType === "profilePicture") {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") setPreview(reader.result);
      };
      reader.readAsDataURL(webcamFile);
    } else {
      const data = new FormData();
      data.append("file", webcamFile);
      const response = await reqFunction(uploadEndpoint, data);
      if (response?.code === 200) {
        setPreview(response.data.fileurl);
      } else {
        renderToast(
          response?.farsi_message
            ? response.farsi_message
            : "در بارگذاری تصویر خطایی رخ داد",
          "err"
        );
      }
    }
    closeCamera();
  };

  useEffect(() => () => stopStream(), []);

  const buttonsDisabled = !limitsLoaded;

  return (
    <>
      {/* Controls */}
      <div className="mb-3 flex items-center gap-3">
        <label
          htmlFor="file-upload"
          className={`cursor-pointer rounded-md px-4 py-2 text-white ${
            buttonsDisabled ? "cursor-not-allowed bg-blue-300" : "bg-blue-500"
          }`}
          title={
            limitsLoaded
              ? `حداکثر ${humanBytes(
                  limits!.max_bytes
                )} | مجاز: ${limits!.types.join("، ")}`
              : "در انتظار دریافت محدودیت‌ها از سرور…"
          }
        >
          انتخاب تصویر
        </label>

        <button
          type="button"
          onClick={() => !buttonsDisabled && setIsCameraOpen(true)}
          disabled={buttonsDisabled}
          className={`rounded-md px-4 py-2 text-white ${
            buttonsDisabled ? "cursor-not-allowed bg-green-300" : "bg-green-600"
          }`}
          title={
            limitsLoaded
              ? `حداکثر ${humanBytes(limits!.max_bytes)}`
              : "در انتظار دریافت محدودیت‌ها از سرور…"
          }
        >
          گرفتن با دوربین
        </button>

        <input
          type="file"
          id="file-upload"
          accept={acceptAttr}
          className="hidden"
          onChange={handleFileInputChange}
          disabled={buttonsDisabled}
        />
      </div>

      {/* Hint */}
      <p className="mb-2 text-xs text-gray-500">
        {limitsLoaded ? (
          <>
            حداکثر اندازه: <b>{humanBytes(limits.max_bytes)}</b> | انواع مجاز:{" "}
            <b>{limits.types.join("، ")}</b>
          </>
        ) : limitsError ? (
          <span className="text-red-600">{limitsError}</span>
        ) : (
          "در حال دریافت محدودیت‌ها…"
        )}
      </p>

      {/* Preview */}
      <div className="flex flex-row items-center justify-around">
        <div className="mt-2 h-24 w-24 overflow-hidden rounded-full">
          <img
            src={
              preview
                ? uploaderType === "projectImage"
                  ? process.env.REACT_APP_SAYAL_API_ENDPOINT_MAIN + preview
                  : preview
                : NoImage
            }
            alt="Selected"
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      {/* Webcam modal */}
      {isCameraOpen && (
        <div className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center">
          <div className="w-[92vw] max-w-md rounded-lg bg-white p-4 shadow-lg">
            <video
              ref={videoRef}
              className="aspect-square w-full rounded"
              autoPlay
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={handleCapture}
                disabled={!isReady || isSwitching}
                className={`rounded-md px-4 py-2 text-white ${
                  isReady && !isSwitching
                    ? "bg-blue-600"
                    : "cursor-not-allowed bg-blue-300"
                }`}
              >
                ثبت عکس
              </button>

              <button
                type="button"
                onClick={switchCamera}
                disabled={isSwitching}
                className={`rounded-md px-4 py-2 text-white ${
                  isSwitching ? "cursor-wait bg-amber-300" : "bg-amber-500"
                }`}
              >
                تعویض دوربین
              </button>

              <button
                type="button"
                onClick={closeCamera}
                className="rounded-md bg-gray-300 px-4 py-2 text-gray-800"
              >
                انصراف
              </button>
            </div>

            <p className="mt-2 text-center text-xs text-gray-500">
              روی برخی گوشی‌ها فقط با مجوز HTTPS می‌توان بین دوربین‌ها جابه‌جا
              شد.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FileUploader;
